import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { InvoiceHire } from '../../types';
import Button from '../Form/Button';
import { useClients } from '../../hooks/useClients';
import { useBranches } from '../../hooks/useBranches';

// Dates in the printed invoice always use dd/mm/yyyy with latin digits,
// regardless of the device locale (Arabic devices otherwise render ٠١/٠٢/٢٠٢٦)
const formatPrintDate = (value?: string | null): string =>
  value ? new Date(value).toLocaleDateString('en-GB') : 'N/A';

const formatPrintMonth = (value?: string | null): string =>
  value ? new Date(value).toLocaleDateString('en-GB', { month: '2-digit', year: 'numeric' }) : 'N/A';

interface InvoiceHireLetterGeneratorProps {
  invoiceHire: InvoiceHire;
  onClose: () => void;
  onGenerate: (data: any) => void;
}

const InvoiceHireLetterGenerator: React.FC<InvoiceHireLetterGeneratorProps> = ({
  invoiceHire,
  onClose,
  onGenerate
}) => {
  const [generatedTemplate, setGeneratedTemplate] = useState<string>('');
  const { data: clientsData } = useClients();
  const { data: branchesData } = useBranches();

  const {
    formState: { isSubmitting }
  } = useForm();

  const getClientInfo = useCallback((clientId: string) => {
    if (!clientsData || clientsData.length === 0) return undefined;
    return clientsData.find((client: any) => (client.documentId || client.id) === clientId);
  }, [clientsData]);

  // The vessel field stores a documentId; show the branch name in the print
  const getVesselName = useCallback(() => {
    const populated = (invoiceHire as any).vessel;
    if (populated && typeof populated === 'object' && populated.name) return populated.name;
    const match = (branchesData || []).find(
      (b: any) => b.documentId === invoiceHire.vesselId || String(b.id) === String(invoiceHire.vesselId)
    );
    return match?.name || 'N/A';
  }, [invoiceHire, branchesData]);

  const generateTemplate = useCallback(() => {
    const client = getClientInfo(invoiceHire.customerId);
    
    const template = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Invoice - ${invoiceHire.invoiceNumber}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
        .header { text-align: center; margin-bottom: 30px; }
        .company-logo { display: flex; align-items: center; justify-content: center; margin-bottom: 20px; }
        .logo-img { width: 50px; height: 50px; margin-right: 10px; }
        .company-name { font-size: 18px; font-weight: bold; color: #c41e3a; }
        .company-subtitle { font-size: 12px; color: #666; }
        .title { font-size: 18px; font-weight: bold; text-align: center; margin: 20px 0; text-decoration: underline; }
        .info-section { margin: 20px 0; }
        .info-table { width: 100%; border-collapse: collapse; margin: 10px 0; }
        .info-table td { border: 1px solid #000; padding: 5px; }
        .info-table .label { background-color: #f0f0f0; font-weight: bold; width: 15%; }
        .items-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        .items-table th, .items-table td { border: 1px solid #000; padding: 5px; text-align: center; font-size: 11px; }
        .items-table th { background-color: #f0f0f0; font-weight: bold; }
        .items-table .description { text-align: left; }
        .signatures { display: flex; justify-content: space-between; margin-top: 40px; }
        .signature-box { width: 45%; text-align: center; }
        .signature-title { font-weight: bold; margin-bottom: 30px; }
        .signature-line { border-bottom: 1px solid #000; margin: 10px 0; height: 30px; }
        .total-section { margin: 20px 0; text-align: right; }
        .total-line { font-size: 14px; font-weight: bold; margin: 5px 0; }
        @media print {
            body { margin: 0; }
            .no-print { display: none; }
        }
    </style>
</head>
<body>
    <div class="company-logo">
        <div class="logo-img" style="background: #c41e3a; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold;">P</div>
        <div>
            <div class="company-name">PetroMarine</div>
            <div class="company-subtitle">Offshore Projects Support Co.</div>
        </div>
    </div>
    
    <div class="title">INVOICE</div>
    
    <table class="info-table">
        <tr>
            <td class="label">Invoice No. :</td>
            <td>${invoiceHire.invoiceNumber}</td>
            <td class="label">Customer :</td>
            <td>${client?.name || 'Unknown Client'}</td>
            <td class="label">Mode of Pay:</td>
            <td>${invoiceHire.modeOfPay.replace('_', ' ').toUpperCase()}</td>
        </tr>
        <tr>
            <td class="label">Invoice Date :</td>
            <td>${formatPrintDate(invoiceHire.invoiceDate)}</td>
            <td class="label">${invoiceHire.contractNumber ? 'Contract No.' : 'FOA No.'} :</td>
            <td>${invoiceHire.contractNumber || (invoiceHire as any).foa?.foaNumber || invoiceHire.projectId || 'N/A'}</td>
            <td class="label">Tel. :</td>
            <td>${invoiceHire.telephone || 'N/A'}</td>
        </tr>
        <tr>
            <td class="label">Operation Date :</td>
            <td>${formatPrintMonth(invoiceHire.operationDate)}</td>
            <td class="label">Vessel :</td>
            <td colspan="3">${getVesselName()}</td>
        </tr>
    </table>
    
    <table class="items-table">
        <thead>
            <tr>
                <th style="width: 5%;">#</th>
                <th style="width: 25%;">Description</th>
                <th style="width: 12%;">From</th>
                <th style="width: 12%;">To</th>
                <th style="width: 10%;">Unit</th>
                <th style="width: 8%;">Qty.</th>
                <th style="width: 12%;">Rate<br/>USD</th>
                <th style="width: 16%;">Amount<br/>USD</th>
            </tr>
        </thead>
        <tbody>
            ${(invoiceHire.items || []).map(item => `
                <tr>
                    <td>${item.itemNumber}</td>
                    <td class="description">${item.description.replace(/\n/g, '<br>')}</td>
                    <td>${formatPrintDate(item.fromDate)}</td>
                    <td>${formatPrintDate(item.toDate)}</td>
                    <td>${item.unit}</td>
                    <td>${item.quantity}</td>
                    <td>${item.rateUSD.toFixed(2)}</td>
                    <td>${item.amountUSD.toFixed(2)}</td>
                </tr>
            `).join('')}
            ${Array.from({ length: Math.max(0, 3 - (invoiceHire.items?.length || 0)) }, (_, i) => `
                <tr>
                    <td>${(invoiceHire.items?.length || 0) + i + 1}</td>
                    <td class="description"></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                </tr>
            `).join('')}
            <tr style="border-top: 2px solid #000;">
                <td colspan="7" style="text-align: right; font-weight: bold; background-color: #f0f0f0;">Net Total</td>
                <td style="font-weight: bold; background-color: #f0f0f0;">${invoiceHire.netTotal.toFixed(2)}</td>
            </tr>
        </tbody>
    </table>
    
    <div class="signatures">
        <div class="signature-box">
            <div class="signature-title">Finance Dep.</div>
            <div class="signature-line"></div>
            <div>${invoiceHire.financeManager?.name || 'N/A'}</div>
        </div>
        
        <div class="signature-box">
            <div class="signature-title">General Manager</div>
            <div class="signature-line"></div>
            <div>${invoiceHire.generalManager?.name || 'N/A'}</div>
        </div>
    </div>
    
    <div class="no-print" style="margin-top: 30px; text-align: center;">
        <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer; margin-right: 10px;">
            Print Invoice
        </button>
        <button onclick="window.close()" style="padding: 10px 20px; font-size: 16px; background: #6c757d; color: white; border: none; border-radius: 4px; cursor: pointer;">
            Close
        </button>
    </div>
</body>
</html>
    `;
    
    setGeneratedTemplate(template);
  }, [invoiceHire, getClientInfo, getVesselName]);

  useEffect(() => {
    generateTemplate();
  }, [generateTemplate]);

  const handleGenerate = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(generatedTemplate);
      printWindow.document.close();
    }
    onGenerate({ template: generatedTemplate });
  };

  const handleDownload = () => {
    const blob = new Blob([generatedTemplate], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${invoiceHire.invoiceNumber}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Generate Invoice</h1>
          <p className="text-gray-600">
            Invoice: {invoiceHire.invoiceNumber} - {invoiceHire.type.charAt(0).toUpperCase() + invoiceHire.type.slice(1)}
          </p>
        </div>
      </div>

      {/* Preview */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Invoice Preview</h2>
        </div>
        <div className="card-body">
          <div 
            className="border rounded-lg p-4 bg-white"
            style={{ minHeight: '700px' }}
            dangerouslySetInnerHTML={{ __html: generatedTemplate }}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-4">
        <Button type="button" onClick={onClose} variant="secondary">
          Cancel
        </Button>
        <Button type="button" onClick={handleDownload} variant="secondary">
          Download HTML
        </Button>
        <Button 
          type="button" 
          onClick={handleGenerate} 
          variant="primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Generating...' : 'Print Invoice'}
        </Button>
      </div>
    </div>
  );
};

export default InvoiceHireLetterGenerator;

